import classNames from 'classnames';
import _debounce from 'lodash/debounce';
import {TPropsWithClassName} from 'core/types';
import React, {useCallback, useEffect} from 'react';
import {memo, PropsWithChildren, useMemo, useRef, useState} from 'react';
import {TTypographyVariant, Typography} from 'components/Typography';

import style from './style.module.scss';
import {DEBOUNCE_DELAY} from 'core/constants';

interface IProps {
    maxLines?: number;
    expandedButtonText?: string;
    collapsedButtonText?: string;
    onChange?: (isExpanded: boolean) => void;
    buttonTextVariant?: TTypographyVariant;
    disableExpand?: boolean;
    typographyClassName?: string;
}

const MAX_LINES_DEFAULT = 5;

export const CollapseText = memo<TPropsWithClassName<PropsWithChildren<IProps>>>(
    ({
        maxLines = MAX_LINES_DEFAULT,
        className,
        typographyClassName,
        children,
        collapsedButtonText = 'подробнее',
        expandedButtonText = 'Свернуть',
        onChange,
        buttonTextVariant = 'badge-s',
        disableExpand = false,
    }) => {
        const [clamped, setClamped] = useState(true);
        const [showButton, setShowButton] = useState(false);
        const childRef = useRef<HTMLDivElement | null>(null);
        const [minHeight, setMinHeight] = useState<number>(0);
        const [maxHeight, setMaxHeight] = useState(0);

        const hasClamping = useCallback((element: HTMLDivElement) => {
            const {clientHeight, scrollHeight} = element;
            return clientHeight !== scrollHeight;
        }, []);

        const calculateHeights = useCallback(() => {
            if (childRef?.current) {
                setMinHeight(childRef.current.offsetHeight);
                setMaxHeight(childRef.current.scrollHeight);
            }
        }, [childRef]);

        const checkButtonAvailability = useCallback(() => {
            if (childRef?.current && clamped) {
                setShowButton(hasClamping(childRef.current));
            }
        }, [clamped, hasClamping]);

        const resizeCb = useCallback(() => {
            checkButtonAvailability();
            calculateHeights();
        }, [calculateHeights, checkButtonAvailability]);

        useEffect(() => {
            const debouncedCheck = _debounce(resizeCb, DEBOUNCE_DELAY);
            resizeCb();
            window.addEventListener('resize', debouncedCheck);

            return () => {
                window.removeEventListener('resize', debouncedCheck);
            };
        }, [resizeCb]);

        const handleClick = (e: React.MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();
            if (disableExpand) {
                return;
            }
            const isExpanded = !clamped;
            setClamped(isExpanded);
            onChange?.(isExpanded);
        };

        const styleInline = useMemo(
            () =>
                clamped
                    ? {WebkitLineClamp: maxLines, height: Boolean(minHeight) ? minHeight : 'auto'}
                    : {height: maxHeight},
            [clamped, maxHeight, maxLines, minHeight],
        );

        return (
            <>
                <div className={classNames(style.longText, className)} style={styleInline}>
                    <div ref={childRef}>{children}</div>
                </div>
                {showButton && (
                    <Typography
                        className={classNames(style.button, 'collapse-text-button', typographyClassName)}
                        onClick={handleClick}
                        variant={buttonTextVariant}
                    >
                        {clamped ? collapsedButtonText : expandedButtonText}
                    </Typography>
                )}
            </>
        );
    },
);
